import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useSystemFetch, useSystemInfo } from '#layers/base/public';

export interface AcceptTermsPayload {
	fullName: string;
	acceptTerms: boolean;
	acceptMarketing: boolean;
}

export const useOnboardingStore = defineStore('onboarding', () => {
	const systemInfo = useSystemInfo();
	const fetch = useSystemFetch();
	// Seeded local systems are already complete. Keep forcing only until this browser accepts terms.
	const forceOnboardingInDev = ref(import.meta.dev && import.meta.env.VITE_FORCE_ONBOARDING === '1');
	const acceptTermsLoading = ref(false);
	const acceptTermsError = ref<unknown | null>(null);

	function overrideOnboardingResult(value: { requireAcceptTerms?: boolean }) {
		const onboarding = systemInfo.onboarding;
		if (onboarding) Object.assign(onboarding, value);
	}

	async function acceptTerms(payload: AcceptTermsPayload): Promise<boolean> {
		acceptTermsError.value = null;
		acceptTermsLoading.value = true;

		try {
			await fetch('system/bootstrap', { method: 'POST', body: payload });
			await systemInfo.refresh();
			forceOnboardingInDev.value = false;
			return true;
		}
		catch (error) {
			acceptTermsError.value = error;
			return false;
		}
		finally {
			acceptTermsLoading.value = false;
		}
	}

	return {
		acceptTermsLoading: computed(() => acceptTermsLoading.value),
		acceptTermsError: computed(() => acceptTermsError.value),
		overrideOnboardingResult,
		acceptTerms,
		clearAcceptTermsError: () => {
			acceptTermsError.value = null;
		},
		requireCreateUser: computed(() => systemInfo.onboarding?.requireCreateUser === true),
		// VITE_FORCE_ONBOARDING=1 opens consent even when seeded local data says setup is complete.
		requireAcceptTerms: computed(() => import.meta.dev ? forceOnboardingInDev.value : systemInfo.onboarding?.requireAcceptTerms === true),
	};
});
